(() => {
  class CozyProduct {
    constructor(root) {
      if (root.dataset.productReady) return;
      root.dataset.productReady = 'true';
      this.root = root;
      this.config = JSON.parse(root.querySelector('[data-product-config]').textContent);
      this.form = root.querySelector('[data-product-form]');
      this.variantInput = root.querySelector('[data-variant-input]');
      this.options = [...root.querySelectorAll('[data-product-option]')];
      this.quantity = root.querySelector('[data-product-quantity]');
      this.plans = [...root.querySelectorAll('[name="selling_plan"]')];
      this.button = root.querySelector('[data-product-submit]');
      this.buttonLabel = root.querySelector('[data-product-submit-label]');
      this.availability = root.querySelector('[data-product-availability]');
      this.zoom = root.querySelector('[data-product-zoom-dialog]');
      this.currency = new Intl.NumberFormat(this.config.locale || 'en-US', { style: 'currency', currency: this.config.currency || 'USD' });
      this.variantInput.disabled = false;
      const controls = root.querySelector('[data-product-options]');
      if (controls) controls.hidden = false;
      this.options.forEach(option => option.addEventListener('change', () => this.updateVariant(true)));
      this.plans.forEach(plan => plan.addEventListener('change', () => this.updatePrice(true)));
      document.addEventListener('product:refresh', event => {
        if (event.detail?.form === this.form) this.updatePrice(false, true);
      });
      this.quantity.addEventListener('input', () => this.updateQuantityButtons());
      root.querySelectorAll('[data-quantity-change]').forEach(button => button.addEventListener('click', () => {
        const step = Number(this.quantity.step) || 1;
        const min = Number(this.quantity.min) || 1;
        const max = Number(this.quantity.max) || Infinity;
        const next = Math.min(max, Math.max(min, (Number(this.quantity.value) || min) + Number(button.dataset.quantityChange) * step));
        this.quantity.value = String(next);
        this.quantity.dispatchEvent(new Event('change', { bubbles: true }));
        this.updateQuantityButtons();
      }));
      root.querySelectorAll('[data-product-thumb]').forEach(button => button.addEventListener('click', () => this.showMedia(Number(button.dataset.productThumb))));
      root.querySelectorAll('[data-product-zoom]').forEach(button => button.addEventListener('click', () => this.openZoom(Number(button.dataset.productZoom))));
      root.querySelector('[data-product-zoom-close]').addEventListener('click', () => this.zoom.close());
      this.zoom.addEventListener('click', event => { if (event.target === this.zoom) this.zoom.close(); });
      this.form.addEventListener('submit', event => {
        this.updateQuantityButtons();
        if (!this.variant || !this.variant.available || this.button.disabled || !this.quantity.checkValidity()) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.quantity.reportValidity();
        }
      }, true);
      const description = root.querySelector('[data-product-description]');
      if (description) {
        const normalized = value => value.normalize('NFKC').toLocaleLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
        const tasting = root.querySelector('.product-tasting');
        const tastingText = normalized(tasting?.querySelector('p')?.textContent || '');
        const descriptionNotes = [...description.querySelectorAll('.cn-composer__notes')].map(notes => normalized([...notes.childNodes].map(node => node.textContent).join(' ')));
        description.querySelectorAll('dl>div').forEach(row => {
          if (normalized(row.querySelector('dt')?.textContent || '') === 'tasting notes') descriptionNotes.push(normalized(row.querySelector('dd')?.textContent || ''));
        });
        if (tastingText && descriptionNotes.includes(tastingText)) tasting.hidden = true;
        description.querySelectorAll('details').forEach(details => {
          const title = details.querySelector('summary');
          if (!title || !['why it belongs in the collection', 'two roast levels one house blend'].includes(normalized(title.textContent))) return;
          const story = document.createElement('section');
          story.className = 'product-story-note';
          if (details.id) story.id = details.id;
          const heading = document.createElement('h3');
          heading.append(...title.childNodes);
          title.remove();
          story.append(heading, ...details.childNodes);
          details.replaceWith(story);
        });
        const grindDetails = [...description.querySelectorAll('details')].find(details => ['find your grind', 'choose your grind', 'whole bean or espresso'].includes(normalized(details.querySelector('summary')?.textContent || '')));
        root.querySelectorAll('[data-product-grind-link]').forEach(link => {
          if (grindDetails) {
            grindDetails.id ||= `ProductGrind-${this.config.productId}`;
            link.setAttribute('href', `#${grindDetails.id}`);
            link.addEventListener('click', () => { grindDetails.open = true; });
          } else {
            const brewing = root.querySelector('.product-brewing');
            if (brewing?.id) link.setAttribute('href', `#${brewing.id}`);
          }
        });
        const existingPreparation = root.querySelector('[data-existing-preparation]');
        if (existingPreparation) {
          const preparation = [...description.querySelectorAll('details')].find(details => normalized(details.querySelector('summary')?.textContent || '') === normalized(existingPreparation.dataset.existingPreparation));
          if (preparation) {
            const previousId = existingPreparation.id;
            const targetId = preparation.id || previousId;
            existingPreparation.removeAttribute('id');
            preparation.id = targetId;
            const extra = existingPreparation.querySelector('[data-preparation-extra]');
            if (extra) {
              if (!normalized(preparation.textContent).includes(normalized(extra.textContent))) preparation.append(extra);
              else extra.remove();
            }
            root.querySelectorAll('a[href]').forEach(link => {
              if (link.getAttribute('href') !== `#${previousId}`) return;
              link.setAttribute('href', `#${targetId}`);
              link.addEventListener('click', () => { preparation.open = true; });
            });
            existingPreparation.hidden = !existingPreparation.querySelector('a,img,details,p');
          }
        }
        const query = window.matchMedia('(min-width: 900px)');
        const disclosures = [...description.querySelectorAll('details')];
        if (disclosures.length > 1) {
          const overview = document.createElement('div');
          overview.className = 'product-description__overview';
          overview.append(...description.childNodes);
          const facts = document.createElement('div');
          facts.className = 'product-description__facts';
          disclosures.forEach(details => facts.append(details));
          description.append(overview, facts);
          description.classList.add('product-description--structured');
        }
        const updateDisclosures = () => disclosures.forEach(details => {
          details.open = query.matches;
          const summary = details.querySelector('summary');
          if (summary) { if (query.matches) summary.setAttribute('tabindex', '-1'); else summary.removeAttribute('tabindex'); }
        });
        updateDisclosures();
        if (location.hash) disclosures.find(details => `#${details.id}` === location.hash)?.setAttribute('open', '');
        query.addEventListener('change', updateDisclosures);
      }
      this.updateVariant(false);
    }
    money(cents) { return this.currency.format(cents / 100); }
    updateVariant(updateUrl) {
      const chosen = this.options.map(option => option.value);
      this.variant = this.options.length ? this.config.variants.find(variant => variant.options.every((value, i) => value === chosen[i])) : this.config.variants.find(variant => variant.id === this.config.initialVariant) || this.config.variants[0];
      this.variantInput.value = this.variant ? String(this.variant.id) : '';
      this.variantInput.disabled = !this.variant;
      if (this.variant) {
        const rule = this.variant.quantityRule || {};
        this.quantity.min = String(rule.min || 1);
        this.quantity.step = String(rule.increment || 1);
        if (rule.max) this.quantity.max = String(rule.max); else this.quantity.removeAttribute('max');
        const min = Number(this.quantity.min);
        const step = Number(this.quantity.step);
        const value = Number(this.quantity.value) || min;
        this.quantity.value = String(Math.max(min, min + Math.ceil((value - min) / step) * step));
        if (rule.max && Number(this.quantity.value) > rule.max) this.quantity.value = String(rule.max);
        if (this.variant.mediaId) this.showMedia(this.variant.mediaId);
      }
      const selected = this.plans.find(plan => plan.checked);
      this.plans.forEach(plan => {
        if (!plan.value) return;
        const allocation = this.variant?.allocations.find(allocation => String(allocation.id) === plan.value);
        plan.disabled = !allocation;
        const label = plan.closest('[data-plan-label]');
        if (label) label.classList.toggle('is-unavailable', !allocation);
        const price = this.root.querySelector(`[data-plan-price="${plan.value}"]`);
        if (price) price.textContent = allocation ? this.money(allocation.price) : '—';
      });
      if (selected?.disabled) {
        selected.checked = false;
        const fallback = this.plans.find(plan => !plan.disabled && !plan.value) || this.plans.find(plan => !plan.disabled);
        if (fallback) fallback.checked = true;
      }
      if (this.config.requiresSellingPlan && !this.plans.some(plan => plan.checked && !plan.disabled)) {
        const availablePlan = this.plans.find(plan => !plan.disabled);
        if (availablePlan) availablePlan.checked = true;
      }
      this.updateQuantityButtons();
      this.updatePrice(updateUrl);
      this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    updatePrice(updateUrl, preserveError = false) {
      const variant = this.variant;
      const plan = this.plans.find(input => input.checked && !input.disabled);
      const allocation = variant?.allocations.find(allocation => String(allocation.id) === plan?.value);
      const unavailablePlan = this.config.requiresSellingPlan && !allocation;
      const isPurchasable = Boolean(variant?.available && !unavailablePlan);
      this.button.disabled = !isPurchasable;
      const state = !variant ? this.config.labels.unavailable : unavailablePlan ? this.config.labels.planUnavailable : !variant.available ? this.config.labels.soldOut : '';
      this.buttonLabel.textContent = isPurchasable ? this.config.labels.add : state;
      this.availability.textContent = state;
      this.availability.hidden = !state;
      const price = this.root.querySelector('[data-product-price]');
      const compare = this.root.querySelector('[data-product-compare]');
      if (variant) {
        const amount = allocation?.price ?? variant.price;
        const compareAmount = allocation?.compareAtPrice ?? variant.compareAtPrice;
        price.textContent = this.money(amount);
        compare.hidden = !(compareAmount > amount);
        compare.textContent = compareAmount ? this.money(compareAmount) : '';
        const oneTime = this.root.querySelector('[data-onetime-price]');
        if (oneTime) oneTime.textContent = this.money(variant.price);
      } else { price.textContent = '—'; compare.hidden = true; }
      const paymentTerms = this.root.querySelector('[data-product-payment-terms]');
      if (paymentTerms) paymentTerms.hidden = Boolean(allocation);
      const error = this.root.querySelector('[data-product-error]');
      if (!preserveError) { error.hidden = true; error.textContent = ''; }
      if (updateUrl && variant) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        if (allocation) url.searchParams.set('selling_plan', allocation.id); else url.searchParams.delete('selling_plan');
        history.replaceState({}, '', url);
      }
      this.root.dispatchEvent(new CustomEvent('product:variant-change', { bubbles: true, detail: { variant, allocation } }));
    }
    updateQuantityButtons() {
      const count = Number(this.quantity.value) || Number(this.quantity.min) || 1;
      this.root.querySelector('[data-quantity-change="-1"]').disabled = count <= (Number(this.quantity.min) || 1);
      this.root.querySelector('[data-quantity-change="1"]').disabled = this.quantity.max !== '' && count >= Number(this.quantity.max);
    }
    showMedia(id) {
      if (!this.root.querySelector(`[data-product-media="${id}"]`)) return;
      this.root.querySelectorAll('[data-product-media]').forEach(slide => {
        const active = Number(slide.dataset.productMedia) === Number(id);
        slide.hidden = !active;
        slide.classList.toggle('is-active', active);
        if (!active) slide.querySelectorAll('video').forEach(video => video.pause());
      });
      this.root.querySelectorAll('[data-product-thumb]').forEach(thumb => thumb.setAttribute('aria-pressed', String(Number(thumb.dataset.productThumb) === Number(id))));
    }
    openZoom(id) {
      const media = this.config.media.find(media => Number(media.id) === id);
      if (!media) return;
      const image = this.root.querySelector('[data-product-zoom-image]');
      image.src = media.src; image.alt = media.alt;
      if (media.width) image.width = media.width;
      if (media.height) image.height = media.height;
      this.zoom.showModal();
    }
  }
  const init = (scope = document) => {
    scope.querySelectorAll('[data-product-section]').forEach(root => new CozyProduct(root));
    scope.querySelectorAll('[data-product-recommendations]').forEach(root => {
      if (root.dataset.loaded || !root.dataset.url) return;
      const load = async () => {
        root.dataset.loaded = 'true';
        try {
          const response = await fetch(root.dataset.url);
          if (!response.ok) return;
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const content = html.querySelector('[data-product-recommendations]');
          if (content?.querySelector('.product-recommendations__grid')) root.replaceChildren(...content.childNodes);
        } catch (_) {}
      };
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) { observer.disconnect(); load(); }
        }, { rootMargin: '300px' });
        observer.observe(root);
      } else load();
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init()); else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
